import { useFormContext } from 'react-hook-form';
import { WizardSummary } from '@/components/wizards';
import {
  EDUCATION_QUALIFICATION_OPTIONS,
  EMPLOYMENT_TYPE_OPTIONS,
  GENDER_OPTIONS,
  LIFE_BENEFIT_OPTION_OPTIONS,
  MEDICAL_STATUS_OPTIONS,
  PLAN_VARIANT_OPTIONS,
  PREMIUM_PAYMENT_FREQUENCY_OPTIONS,
  PROPOSER_RELATIONSHIP_OPTIONS,
  RESIDENCE_STATUS_OPTIONS,
  type LifeQuoteFormData,
} from '../../schema';
import { formatCurrency } from '@/utils';

interface ReviewStepProps {
  proposerPhone: string;
  proposerEmail?: string;
}

const YES_NO_LABEL: Record<string, string> = { yes: 'Yes', no: 'No' };

function labelFor(options: { value: string; label: string }[], value?: string): string {
  return options.find((o) => o.value === value)?.label || '-';
}

function currency(value?: string): string {
  if (!value) return '-';
  const n = Number(value);
  return Number.isFinite(n) ? formatCurrency(n) : '-';
}

export function ReviewStep({ proposerPhone, proposerEmail }: ReviewStepProps) {
  const { watch } = useFormContext<LifeQuoteFormData>();
  const data = watch();

  const proposerName = [data.first_name, data.middle_name, data.last_name].filter(Boolean).join(' ');
  const lifeAssuredName = [
    data.life_assured_first_name,
    data.life_assured_middle_name,
    data.life_assured_last_name,
  ]
    .filter(Boolean)
    .join(' ');

  const sections = [
    {
      title: 'Proposer',
      items: [
        { label: 'Name', value: proposerName || '-' },
        { label: 'Gender', value: labelFor(GENDER_OPTIONS, data.gender) },
        { label: 'Date of Birth', value: data.date_of_birth || '-' },
        { label: 'Age', value: data.age !== undefined ? `${data.age} years` : '-' },
        { label: 'Phone', value: proposerPhone || '-' },
        { label: 'Email', value: proposerEmail || '-' },
      ],
    },
    {
      title: 'Life Assured',
      items:
        data.life_assured_same_as_proposer === 'yes'
          ? [{ label: 'Same as Proposer', value: 'Yes' }]
          : [
              { label: 'Name', value: lifeAssuredName || '-' },
              { label: 'Gender', value: labelFor(GENDER_OPTIONS, data.life_assured_gender) },
              { label: 'Date of Birth', value: data.life_assured_date_of_birth || '-' },
              {
                label: 'Age',
                value: data.life_assured_age !== undefined ? `${data.life_assured_age} years` : '-',
              },
            ],
    },
    {
      title: 'Location & Servicing',
      items: [
        { label: 'Residence Status', value: labelFor(RESIDENCE_STATUS_OPTIONS, data.residence_status) },
        { label: 'PIN Code', value: data.pincode || '-' },
        { label: 'Proposer State', value: data.proposer_state_name || '-' },
        { label: 'Sales Person State', value: data.sales_person_state_name || '-' },
      ],
    },
    {
      title: 'Policy',
      items: [
        { label: 'Policy Term', value: data.policy_term_years ? `${data.policy_term_years} years` : '-' },
        {
          label: 'Premium Payment Term',
          value: data.premium_payment_term_years ? `${data.premium_payment_term_years} years` : '-',
        },
        {
          label: 'Premium Payment Frequency',
          value: labelFor(PREMIUM_PAYMENT_FREQUENCY_OPTIONS, data.premium_payment_frequency),
        },
        {
          label: 'Proposer Relationship',
          value: labelFor(PROPOSER_RELATIONSHIP_OPTIONS, data.proposer_relationship),
        },
        { label: 'Plan Variant', value: labelFor(PLAN_VARIANT_OPTIONS, data.plan_variant) },
        {
          label: 'Life Benefit Option',
          value: labelFor(LIFE_BENEFIT_OPTION_OPTIONS, data.life_benefit_option),
        },
      ],
    },
    {
      title: 'Eligibility & Health',
      items: [
        { label: 'Existing Customer', value: YES_NO_LABEL[data.existing_customer] || '-' },
        { label: 'First Time Buyer', value: YES_NO_LABEL[data.first_time_buyer] || '-' },
        { label: 'Premium Holiday', value: YES_NO_LABEL[data.premium_holiday] || '-' },
        { label: 'Medical / Non-Medical', value: labelFor(MEDICAL_STATUS_OPTIONS, data.medical_non_medical) },
        {
          label: 'Smoking / Tobacco Usage',
          value: YES_NO_LABEL[data.smoking_tobacco_usage] || '-',
        },
      ],
    },
    {
      title: 'Occupation & Finances',
      items: [
        { label: 'Occupation', value: data.occupation || '-' },
        { label: 'Employment Type', value: labelFor(EMPLOYMENT_TYPE_OPTIONS, data.employment_type) },
        {
          label: 'Education Qualification',
          value: labelFor(EDUCATION_QUALIFICATION_OPTIONS, data.education_qualification),
        },
        { label: 'Annual Income', value: currency(data.annual_income) },
        { label: 'Sum Assured', value: currency(data.sum_assured) },
      ],
    },
  ];

  return (
    <div>
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Review & Submit</h2>
      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 mb-6">
        Confirm the details below before requesting quotes.
      </p>
      <WizardSummary sections={sections} />
    </div>
  );
}
